/**
 * FunnelChart — W66.X13 rewrite.
 *
 * TRUE inverted-trapezoid funnel. Previous W66.X12 version stacked rectangles
 * of decreasing width, which is bar-chart styling, not a funnel. Bryan flagged
 * that the view should look like a funnel at all times regardless of values.
 *
 * Implementation: one SVG. Each stage is a horizontal slice of a continuous
 * trapezoid whose left and right edges descend on straight diagonals from full
 * width at the top (TOUCHES) to a narrow neck at the bottom (OPPORTUNITIES
 * CREATED). The diagonal edges are SHARED between adjacent stages, so the
 * silhouette reads as one solid funnel.
 *
 * Stage volumes drive only the numbers shown inside each slice. The shape
 * itself is fixed and always present, even when every stage is zero or null.
 *
 * Stage mapping from mql_events / Supabase:
 *  - TOUCHES:               cadence_step_completions count (30-day proxy)
 *  - ENGAGEMENTS:           csc with positive outcome
 *  - MQLS CREATED:          mql_events stage=MQL
 *  - MQLS ACCEPTED BY BDR:  mql_events stage=SAL
 *  - MEETINGS BOOKED:       calendar_events (graceful fail)
 *  - OPPORTUNITIES CREATED: mql_events stage=OPPORTUNITY
 *
 * Palette (W66.X12 canon, retained):
 *   gray → gray-mid → yellow → orange → red → green
 */

import { useEffect, useState } from "react";
import { api, supabase, getCurrentTenant, getSession } from "@/lib/session";
import { BDR_ACCEPTANCE_TARGET_PCT } from "@/data/marketing-targets";

const TENANT_ID = 1;

interface FunnelStage {
  label: string;
  volume: number | null;
  conversionPct: number | null;
}

// Canon palette per CANON_VISUALS_LOCKED.md. Top of funnel is upstream raw
// volume (gray); bottom is the goal state (green). Middle stages step through
// yellow → orange → red.
const STAGE_COLORS = [
  "#6B7280", // TOUCHES                 — gray
  "#9CA3AF", // ENGAGEMENTS             — gray-mid
  "#EAB308", // MQLS CREATED            — yellow
  "#EA580C", // MQLS ACCEPTED BY BDR    — orange
  "#DC2626", // MEETINGS BOOKED         — red
  "#16A34A", // OPPORTUNITIES CREATED   — green (goal)
];

function pct(num: number | null, denom: number | null): number | null {
  if (!num || !denom || denom === 0) return null;
  return Math.round((num / denom) * 100);
}

// ───────────────────────────────────────────────────────────────────────────
// SVG geometry. Single canvas, continuous trapezoid.
// ───────────────────────────────────────────────────────────────────────────

const SVG_W = 1000;
const SVG_H = 560;

// Funnel silhouette: full width at top (TOUCHES), narrow neck at bottom
// (OPPORTUNITIES CREATED). The neck stays wide enough (~28% of SVG_W) to fit
// the bottom-stage label and count comfortably.
const TOP_LEFT_X     = 40;
const TOP_RIGHT_X    = SVG_W - 40;
const BOTTOM_LEFT_X  = SVG_W * 0.36;
const BOTTOM_RIGHT_X = SVG_W * 0.64;

const STAGE_GAP = 4; // px between adjacent horizontal slices

// Compute the x-coordinates of the funnel walls at a given vertical
// fraction t (0 = top, 1 = bottom).
function wallX(t: number): { left: number; right: number } {
  const left  = TOP_LEFT_X  + (BOTTOM_LEFT_X  - TOP_LEFT_X)  * t;
  const right = TOP_RIGHT_X + (BOTTOM_RIGHT_X - TOP_RIGHT_X) * t;
  return { left, right };
}

function fmt(n: number | null): string {
  if (n == null) return "—";
  return n.toLocaleString();
}

interface SliceProps {
  label: string;
  volume: number | null;
  conversionPct: number | null;
  color: string;
  yTop: number;
  yBottom: number;
  tTop: number;
  tBottom: number;
  showConversion: boolean;
}

function FunnelSlice({
  label,
  volume,
  conversionPct,
  color,
  yTop,
  yBottom,
  tTop,
  tBottom,
  showConversion,
}: SliceProps) {
  const top    = wallX(tTop);
  const bottom = wallX(tBottom);

  // Trapezoid points: top-left, top-right, bottom-right, bottom-left
  const points = [
    `${top.left},${yTop}`,
    `${top.right},${yTop}`,
    `${bottom.right},${yBottom}`,
    `${bottom.left},${yBottom}`,
  ].join(" ");

  // Centerline for text placement
  const cy = (yTop + yBottom) / 2;
  const midLeft  = (top.left  + bottom.left)  / 2;
  const midRight = (top.right + bottom.right) / 2;
  const innerWidth = midRight - midLeft;

  // Text sizes scale with slice width to stay legible at the neck
  const labelSize = innerWidth > 480 ? 13 : innerWidth > 320 ? 12 : 11;
  const countSize = innerWidth > 480 ? 28 : innerWidth > 320 ? 22 : 18;
  const pctSize   = innerWidth > 480 ? 16 : innerWidth > 320 ? 14 : 12;

  return (
    <g>
      <polygon points={points} fill={color} />

      {/* Stage label — uppercase, white on band */}
      <text
        x={(midLeft + midRight) / 2}
        y={cy - countSize * 0.45}
        textAnchor="middle"
        fontSize={labelSize}
        fontWeight={700}
        letterSpacing={1.4}
        fill="rgba(255,255,255,0.92)"
        fontFamily="Inter, system-ui, sans-serif"
        style={{ textTransform: "uppercase" }}
      >
        {label}
      </text>

      {/* Count */}
      <text
        x={(midLeft + midRight) / 2}
        y={cy + countSize * 0.45}
        textAnchor="middle"
        fontSize={countSize}
        fontWeight={800}
        fill="#FFFFFF"
        fontFamily="DM Sans, Inter, system-ui, sans-serif"
        style={{ fontVariantNumeric: "tabular-nums" }}
      >
        {fmt(volume)}
      </text>

      {/* Conversion % — right side of slice, smaller */}
      {showConversion && (
        <>
          <text
            x={midRight - 12}
            y={cy + 4}
            textAnchor="end"
            fontSize={pctSize}
            fontWeight={700}
            fill="rgba(255,255,255,0.95)"
            fontFamily="Inter, system-ui, sans-serif"
            style={{ fontVariantNumeric: "tabular-nums" }}
          >
            {conversionPct != null ? `${conversionPct}%` : "—"}
          </text>
          <text
            x={midRight - 12}
            y={cy + 4 + pctSize * 0.95}
            textAnchor="end"
            fontSize={pctSize * 0.65}
            fontWeight={500}
            fill="rgba(255,255,255,0.72)"
            fontFamily="Inter, system-ui, sans-serif"
          >
            → next stage
          </text>
        </>
      )}
    </g>
  );
}

export function FunnelChart() {
  const [stages, setStages] = useState<FunnelStage[]>([]);
  const [loading, setLoading] = useState(true);
  const tenant = getCurrentTenant();
  const session = getSession();

  useEffect(() => {
    const tenantId = tenant?.tenantId ?? TENANT_ID;
    let cancelled = false;
    setLoading(true);

    Promise.all([
      supabase()
        .from("cadence_step_completions")
        .select("id", { count: "exact", head: true })
        .eq("cadence_enrollments.tenant_id", tenantId),

      supabase()
        .from("cadence_step_completions")
        .select("id", { count: "exact", head: true })
        .eq("cadence_enrollments.tenant_id", tenantId)
        .in("outcome", ["replied", "booked", "interested", "meeting_booked", "responded"]),

      api<{
        totals: { MQL: number | null; SAL: number | null; SQL: number | null; OPPORTUNITY: number | null };
      }>("/api/marketing/funnel").catch(() => null),

      // Supabase query builder is thenable but not a full Promise, so use
      // .then(ok, err) instead of .catch() to avoid a runtime TypeError.
      supabase()
        .from("calendar_events")
        .select("id", { count: "exact", head: true })
        .eq("tenant_id", tenantId)
        .in("status", ["booked", "held"])
        .then(
          (r: any) => r,
          () => ({ count: null, data: null, error: null }),
        ),
    ]).then(([touchRes, engRes, funnelRes, meetingRes]) => {
      if (cancelled) return;

      const touches = touchRes.count ?? null;
      const engagements = engRes.count ?? null;
      const mqls = funnelRes?.totals?.MQL ?? null;
      const accepted = funnelRes?.totals?.SAL ?? null;
      const meetings = (meetingRes as any).count ?? null;
      const opps = funnelRes?.totals?.OPPORTUNITY ?? null;

      const result: FunnelStage[] = [
        { label: "TOUCHES",               volume: touches,     conversionPct: pct(engagements, touches) },
        { label: "ENGAGEMENTS",           volume: engagements, conversionPct: pct(mqls, engagements) },
        { label: "MQLS CREATED",          volume: mqls,        conversionPct: pct(accepted, mqls) },
        { label: "MQLS ACCEPTED BY BDR",  volume: accepted,    conversionPct: pct(meetings, accepted) },
        { label: "MEETINGS BOOKED",       volume: meetings,    conversionPct: pct(opps, meetings) },
        { label: "OPPORTUNITIES CREATED", volume: opps,        conversionPct: null },
      ];

      setStages(result);
      setLoading(false);
    }).catch(() => {
      if (!cancelled) setLoading(false);
    });

    return () => { cancelled = true; };
  }, [tenant?.tenantId, session?.token]);

  // ALWAYS render the funnel silhouette. When loading or empty we render the
  // shape with em-dash placeholders. The view never collapses to a bare box.
  const renderStages: FunnelStage[] = stages.length === 6
    ? stages
    : [
        { label: "TOUCHES",               volume: null, conversionPct: null },
        { label: "ENGAGEMENTS",           volume: null, conversionPct: null },
        { label: "MQLS CREATED",          volume: null, conversionPct: null },
        { label: "MQLS ACCEPTED BY BDR",  volume: null, conversionPct: null },
        { label: "MEETINGS BOOKED",       volume: null, conversionPct: null },
        { label: "OPPORTUNITIES CREATED", volume: null, conversionPct: null },
      ];

  const n = renderStages.length;
  const usableHeight = SVG_H - STAGE_GAP * (n - 1);
  const sliceHeight = usableHeight / n;

  const acceptedStage = renderStages[3];
  const mqlStage = renderStages[2];
  const acceptancePct =
    acceptedStage && mqlStage ? pct(acceptedStage.volume, mqlStage.volume) : null;

  return (
    <div className="w-full">
      <div className="w-full" style={{ opacity: loading ? 0.55 : 1, transition: "opacity 0.2s" }}>
        <svg
          viewBox={`0 0 ${SVG_W} ${SVG_H}`}
          style={{ width: "100%", height: "auto", display: "block" }}
          role="img"
          aria-label="Marketing conversion funnel"
        >
          {renderStages.map((stage, i) => {
            const yTop    = i * (sliceHeight + STAGE_GAP);
            const yBottom = yTop + sliceHeight;
            const tTop    = yTop / SVG_H;
            const tBottom = yBottom / SVG_H;

            return (
              <FunnelSlice
                key={stage.label}
                label={stage.label}
                volume={stage.volume}
                conversionPct={stage.conversionPct}
                color={STAGE_COLORS[i]}
                yTop={yTop}
                yBottom={yBottom}
                tTop={tTop}
                tBottom={tBottom}
                showConversion={i < n - 1}
              />
            );
          })}
        </svg>
      </div>

      <p className="mt-4 text-center text-[11px] text-neutral-500">
        Acceptance at MQL → BDR is the leverage point. Currently{" "}
        <span
          className="font-semibold"
          style={{ color: (acceptancePct ?? 0) >= BDR_ACCEPTANCE_TARGET_PCT ? "#16A34A" : "#DC2626" }}
        >
          {acceptancePct != null ? `${acceptancePct}%` : "—"}
        </span>
        {" "}· target floor {BDR_ACCEPTANCE_TARGET_PCT}%.
      </p>
    </div>
  );
}
